import { actionTypes as types } from './actions';
import { every, some } from 'lodash';
import inputsReducer from './inputs';

const initialFormState = {
  isValid: true, // Indicates if all inputs within the form are valid.
  isDirty: false, // Indicates if any input has been changed since initialization.
  isTouched: false, // Indicates if any input has been focused/unfocused.
  isSubmitted: false, // Indicates if the form has been submitted.
  isSubmitting: false, // Indicates if the form is currently submitting.
  isFailedSubmit: false, // Indicates if the form failed during submission.
  failedSubmitCount: 0, // Indicates how many failed submits have occurred.
                        // It is only positive when isFailedSubmit is true.
  inputs: {} // The information for all individual inputs in the form.
};

/**
 * A reducer for managing the state of a form and all of its inputs.
 */
export function form(state = initialFormState, action) {
  switch (action.type) {
    case types.START_SUBMIT_FORM:
      return {
        ...state,
        isSubmitted: false,
        isSubmitting: true
      };
    case types.COMPLETE_SUBMIT_FORM:
      return {
        ...state,
        isSubmitted: true,
        isSubmitting: false,
        isFailedSubmit: false,
        failedSubmitCount: 0
      };
    case types.FAIL_SUBMIT_FORM:
      return {
        ...state,
        isSubmitted: false,
        isSubmitting: false,
        isFailedSubmit: true,
        failedSubmitCount: state.failedSubmitCount + 1
      };
    default: {
      const inputs = inputsReducer(state.inputs, action);
      if (inputs === state.inputs) {
        return state;
      }
      return {
        ...state,
        isValid: every(inputs, input => input.isValid),
        isDirty: some(inputs, input => input.isDirty),
        isTouched: some(inputs, input => input.isTouched),
        inputs
      };
    }
  }
}

/**
 * Creates a form reducer that will only recieve actions that match the provided
 * form id. This allows multiple forms to exist within a redux tree.
 */
export default function createForm(formId) {
  const scopedInitialFormState = { id: formId, ...initialFormState };
  return (state = scopedInitialFormState, action) => {
    const { payload = {} } = action;
    return payload.formId === formId ? form(state, action) : state;
  };
}
